/**
 * SaveSearchButton — "Save current search" action shown on the search page.
 *
 * Captures the current query text + facet filters from the URL-backed
 * {@link SearchParams} and creates a saved search via the C15 API. The button
 * is disabled until there is a non-empty query, so we never persist an empty
 * search. A short success confirmation links through to the saved-searches
 * list.
 */
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { useCreateSavedSearch } from "./hooks/useSavedSearches";
import type { CreateSavedSearchRequest, SavedSearchType } from "@records-monitor/shared";
import { paramsToFilters, type SearchParams } from "@/features/search/urlState";

export interface SaveSearchButtonProps {
  params: SearchParams;
}

/** Map the search-page mode to the saved-search ``type`` enum. */
function toSavedType(mode: SearchParams["mode"]): SavedSearchType {
  if (mode === "semantic") return "semantic";
  if (mode === "ask") return "agentic";
  return "keyword";
}

export function SaveSearchButton({ params }: SaveSearchButtonProps): ReactElement {
  const create = useCreateSavedSearch();
  const [saved, setSaved] = useState(false);
  const q = params.q?.trim() ?? "";
  const disabled = q.length === 0 || create.isPending;

  const handleSave = (): void => {
    const filters = paramsToFilters(params);
    const body: CreateSavedSearchRequest = {
      type: toSavedType(params.mode),
      query: { q },
      ...(filters ? { filters } : {}),
      dedupWindowSeconds: 86_400,
    };
    create.mutate(body, { onSuccess: () => setSaved(true) });
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<BookmarkAddIcon />}
        onClick={handleSave}
        disabled={disabled}
      >
        Save current search
      </Button>
      {create.isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not save the search. Please try again.
        </Alert>
      ) : null}
      <Snackbar
        open={saved}
        autoHideDuration={6000}
        onClose={() => setSaved(false)}
        message="Search saved"
        action={
          <Button component={Link} to="/saved" color="secondary" size="small">
            View saved
          </Button>
        }
      />
    </>
  );
}
